import { Button, Spinner, Typography } from '@sonnen/web-ui';
import { useAuth } from '@sonnen/web-utils/hooks';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../AppContext/AppContext';
import { AppRoute } from '../../constants';
import { CurrentUser } from '../../graphql/generated';
import { useCurrentUserData } from '../../hooks/useCurrentUserData';
import { useCurrentUserFeatureFlags } from '../../hooks/useCurrentUserFeatureFlags';
import { useTranslation } from '../../i18n/i18n';
import { getRouteAfterLogin } from '../../utils/utils';
import styles from './Login.module.css';

type LoginState = 'loading' | 'loaded' | 'error';

export const Login = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user: authUser, authenticated, login, loading, error } = useAuth<CurrentUser>();
  const { user } = useAppContext();
  const { allowToAccessEnergyStore } = useCurrentUserFeatureFlags();

  const [state, setState] = useState<LoginState>('loading');
  const [currentUser, loadingUserData] = useCurrentUserData();

  const loginDeferrer = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (currentUser || user) {
      setState('loaded');
      const routeAfterLogin = getRouteAfterLogin();
      navigate(
        routeAfterLogin || (allowToAccessEnergyStore ? AppRoute.ENERGY_STORE : AppRoute.ROOT),
      );
    } else if (authUser && !loading && !loadingUserData) {
      window.Sentry?.reportError('Error when logging into Customer Dynamic Tariff');
      setState('error');
    } else {
      setState('loading');
    }
  }, [authUser, currentUser, user, loading, loadingUserData]);

  useEffect(() => {
    const okToLogin =
      !authenticated && !loading && !authUser && !window.location.search.includes('code');

    if (okToLogin) {
      setState('loading');
      // Debounce login to allow for the authentication state to be updated
      loginDeferrer.current = setTimeout(() => login(), 100);
    }

    return () => {
      if (loginDeferrer.current) clearTimeout(loginDeferrer.current);
    };
  }, [authenticated, loading, authUser, login]);

  if (error) {
    window.Sentry?.reportError(
      `Error from useAuth when logging into Customer Dynamic Tariff: ${error.message}`,
    );
  }

  const errorMessage = (
    <div data-testid="error-message" className={styles.errorMessage}>
      <Typography.Label1 variant="alert">{t('login.errorMessage')}</Typography.Label1>
      <Button
        onClick={login}
        label={t('login.retry')}
        size="medium"
        dataTestId="retry-btn"
        variant="secondary"
      />
    </div>
  );

  return (
    <div className={styles.loginPage} data-testid="login-page">
      <div className={styles.loadingAndErrorMessage}>
        {error ? (
          errorMessage
        ) : (
          <div className={styles.loading}>
            {(loading || loadingUserData) && (
              <Spinner size="xl" accessibilityLabel={t('loadingUserDetails')} />
            )}
            {state === 'error' && errorMessage}
          </div>
        )}
      </div>
    </div>
  );
};
